import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LandscapeIcon from '@mui/icons-material/Landscape';
import { getCurrentLandscapeAnalysis, saveCurrentLandscapeAnalysis, fetchCurrentLandscapeAnalysisFromSOW } from '../../utils/discoveryPhaseService';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const CurrentLandscapeAnalysisPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  
  const [analysis, setAnalysis] = useState({
    overview: '',
    currentSystems: [],
    systemIntegrations: [],
    dataLandscape: [],
    infrastructureAssessment: []
  });
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingAnalysis, setEditingAnalysis] = useState({
    overview: '',
    currentSystems: [],
    systemIntegrations: [],
    dataLandscape: [],
    infrastructureAssessment: []
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogItem, setDialogItem] = useState({});
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load current landscape analysis data from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadCurrentLandscapeAnalysisFromDB();
    }
  }, [projectId]);
  
  // Load current landscape analysis from database
  const loadCurrentLandscapeAnalysisFromDB = async () => {
    setLoading(true);
    try {
      const analysisData = await getCurrentLandscapeAnalysis(projectId);
      if (analysisData) {
        setAnalysis(analysisData);
        setEditingAnalysis(analysisData);
      }
    } catch (error) {
      console.error('Error loading current landscape analysis:', error);
      showSnackbar('Error loading current landscape analysis data', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Save current landscape analysis to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveCurrentLandscapeAnalysis(projectId, editingAnalysis);
      setAnalysis(editingAnalysis);
      setEditMode(false);
      showSnackbar('Current landscape analysis saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving current landscape analysis:', error);
      showSnackbar('Error saving current landscape analysis', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch current landscape analysis from project documents using AI
  const handleFetchFromSOW = async () => {
    setAiLoading(true);
    try {
      const response = await fetchCurrentLandscapeAnalysisFromSOW(projectId);
      if (response.data) {
        setEditingAnalysis(response.data);
        setAnalysis(response.data);
        showSnackbar('Current landscape analysis generated from project documents successfully!', 'success');
      }
    } catch (error) {
      console.error('Error fetching current landscape analysis from project documents:', error);
      showSnackbar('Error generating current landscape analysis from project documents', 'error');
    } finally {
      setAiLoading(false);
    }
  };
  
  // Dialog functions
  const handleOpenDialog = (type, item = null, index = null) => {
    setDialogType(type);
    
    if (type === 'currentSystems') {
      setDialogItem(item || { name: '', description: '', version: '', vendor: '', status: 'Active' });
    } else if (type === 'systemIntegrations') {
      setDialogItem(item || { fromSystem: '', toSystem: '', integrationType: '', description: '', status: 'Active' });
    } else if (type === 'dataLandscape') {
      setDialogItem(item || { dataSource: '', dataType: '', volume: '', frequency: '', quality: 'Good' });
    } else if (type === 'infrastructureAssessment') {
      setDialogItem(item || { component: '', description: '', currentCapacity: '', utilizationRate: '', status: 'Healthy' });
    }
    
    setDialogItemIndex(index);
    setOpenDialog(true);
  };
  
  const handleSaveDialog = () => {
    const newData = { ...editingAnalysis };
    
    if (dialogItemIndex !== null) {
      // Edit existing item
      newData[dialogType][dialogItemIndex] = dialogItem;
    } else {
      // Add new item
      newData[dialogType].push(dialogItem);
    }
    
    setEditingAnalysis(newData);
    setOpenDialog(false);
  };
  
  const handleDeleteItem = (type, index) => {
    const newData = { ...editingAnalysis };
    newData[type].splice(index, 1);
    setEditingAnalysis(newData);
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const renderSystemCard = (system, index) => (
    <Card key={index} sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{system.name}</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {system.description}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Version:</strong> {system.version}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Vendor:</strong> {system.vendor}
        </Typography>
        <Chip 
          label={system.status} 
          color={system.status === 'Active' ? 'success' : system.status === 'Deprecated' ? 'error' : 'warning'}
          size="small"
        />
      </CardContent>
      {editMode && (
        <CardActions>
          <IconButton 
            size="small" 
            onClick={() => handleOpenDialog('currentSystems', system, index)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDeleteItem('currentSystems', index)}
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      )}
    </Card>
  );
  
  const renderIntegrationCard = (integration, index) => (
    <Card key={index} sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {integration.fromSystem} → {integration.toSystem}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {integration.description}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Integration Type:</strong> {integration.integrationType}
        </Typography>
        <Chip 
          label={integration.status} 
          color={integration.status === 'Active' ? 'success' : integration.status === 'Inactive' ? 'error' : 'warning'}
          size="small"
        />
      </CardContent>
      {editMode && (
        <CardActions>
          <IconButton 
            size="small" 
            onClick={() => handleOpenDialog('systemIntegrations', integration, index)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDeleteItem('systemIntegrations', index)}
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      )}
    </Card>
  );
  
  const renderDataLandscapeCard = (dataItem, index) => (
    <Card key={index} sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{dataItem.dataSource}</Typography>
        <Typography variant="body2" paragraph>
          <strong>Data Type:</strong> {dataItem.dataType}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Volume:</strong> {dataItem.volume}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Frequency:</strong> {dataItem.frequency}
        </Typography>
        <Chip 
          label={`Quality: ${dataItem.quality}`} 
          color={dataItem.quality === 'Good' ? 'success' : dataItem.quality === 'Poor' ? 'error' : 'warning'}
          size="small"
        />
      </CardContent>
      {editMode && (
        <CardActions>
          <IconButton 
            size="small" 
            onClick={() => handleOpenDialog('dataLandscape', dataItem, index)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDeleteItem('dataLandscape', index)}
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      )}
    </Card>
  );
  
  const renderInfrastructureCard = (infraItem, index) => (
    <Card key={index} sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{infraItem.component}</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {infraItem.description}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Current Capacity:</strong> {infraItem.currentCapacity}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Utilization Rate:</strong> {infraItem.utilizationRate}
        </Typography>
        <Chip 
          label={infraItem.status} 
          color={infraItem.status === 'Healthy' ? 'success' : infraItem.status === 'Critical' ? 'error' : 'warning'}
          size="small"
        />
      </CardContent>
      {editMode && (
        <CardActions>
          <IconButton 
            size="small" 
            onClick={() => handleOpenDialog('infrastructureAssessment', infraItem, index)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDeleteItem('infrastructureAssessment', index)}
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      )}
    </Card>
  );
  
  if (loading && !analysis.overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SectionTitle variant="h4">
            <LandscapeIcon />
            Current Landscape Analysis
          </SectionTitle>
          <Box>
            {!editMode ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleFetchFromSOW}
                  disabled={aiLoading}
                  sx={{ mr: 1, backgroundColor: '#F5821F' }}
                >
                  {aiLoading ? <CircularProgress size={20} /> : 'Fetch From Project Documents'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setEditMode(false);
                    setEditingAnalysis(analysis);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Analyze the current technology landscape, systems, integrations, and infrastructure for {project?.name}.
        </Typography>
      </Paper>
      
      {/* Overview Section */}
      <Accordion expanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {editMode ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Current Landscape Overview"
              value={editingAnalysis.overview}
              onChange={(e) => setEditingAnalysis({
                ...editingAnalysis,
                overview: e.target.value
              })}
            />
          ) : (
            <Typography>
              {analysis.overview || 'No overview available. Click "Edit" to add content or "Fetch From Project Documents" to generate from your project documents.'}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* Current Systems */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Current Systems</Typography>
          <Chip 
            label={editingAnalysis.currentSystems.length} 
            size="small" 
            sx={{ ml: 2 }} 
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('currentSystems')}
                sx={{ mb: 2 }}
              >
                Add System
              </Button>
            )}
            {editingAnalysis.currentSystems.map((system, index) => 
              renderSystemCard(system, index)
            )}
            {editingAnalysis.currentSystems.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No systems defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* System Integrations */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">System Integrations</Typography>
          <Chip 
            label={editingAnalysis.systemIntegrations.length} 
            size="small" 
            sx={{ ml: 2 }} 
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('systemIntegrations')}
                sx={{ mb: 2 }}
              >
                Add Integration
              </Button>
            )}
            {editingAnalysis.systemIntegrations.map((integration, index) => 
              renderIntegrationCard(integration, index)
            )}
            {editingAnalysis.systemIntegrations.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No integrations defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Data Landscape */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Data Landscape</Typography>
          <Chip 
            label={editingAnalysis.dataLandscape.length} 
            size="small" 
            sx={{ ml: 2 }} 
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('dataLandscape')}
                sx={{ mb: 2 }}
              >
                Add Data Source
              </Button>
            )}
            <Grid container spacing={2}>
              {editingAnalysis.dataLandscape.map((dataItem, index) => (
                <Grid item xs={12} md={6} key={index}>
                  {renderDataLandscapeCard(dataItem, index)}
                </Grid>
              ))}
            </Grid>
            {editingAnalysis.dataLandscape.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No data sources defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Infrastructure Assessment */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Infrastructure Assessment</Typography>
          <Chip 
            label={editingAnalysis.infrastructureAssessment.length} 
            size="small" 
            sx={{ ml: 2 }} 
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('infrastructureAssessment')}
                sx={{ mb: 2 }}
              >
                Add Infrastructure Component
              </Button>
            )}
            <Grid container spacing={2}>
              {editingAnalysis.infrastructureAssessment.map((infraItem, index) => (
                <Grid item xs={12} md={6} key={index}>
                  {renderInfrastructureCard(infraItem, index)}
                </Grid>
              ))}
            </Grid>
            {editingAnalysis.infrastructureAssessment.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No infrastructure components defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Dialog for adding/editing items */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex !== null ? 'Edit' : 'Add'} {dialogType.replace(/([A-Z])/g, ' $1').toLowerCase()}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'currentSystems' && (
            <Box>
              <TextField
                fullWidth
                label="System Name"
                value={dialogItem.name || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={dialogItem.description || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Version"
                value={dialogItem.version || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, version: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Vendor"
                value={dialogItem.vendor || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, vendor: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                select
                label="Status"
                value={dialogItem.status || 'Active'}
                onChange={(e) => setDialogItem({ ...dialogItem, status: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="Active">Active</option>
                <option value="Deprecated">Deprecated</option>
                <option value="Planned">Planned</option>
              </TextField>
            </Box>
          )}
          
          {dialogType === 'systemIntegrations' && (
            <Box>
              <TextField
                fullWidth
                label="From System"
                value={dialogItem.fromSystem || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, fromSystem: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="To System"
                value={dialogItem.toSystem || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, toSystem: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Integration Type"
                value={dialogItem.integrationType || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, integrationType: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={dialogItem.description || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                select
                label="Status"
                value={dialogItem.status || 'Active'}
                onChange={(e) => setDialogItem({ ...dialogItem, status: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Planned">Planned</option>
              </TextField>
            </Box>
          )}
          
          {dialogType === 'dataLandscape' && (
            <Box>
              <TextField
                fullWidth
                label="Data Source"
                value={dialogItem.dataSource || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, dataSource: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Data Type"
                value={dialogItem.dataType || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, dataType: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Volume"
                value={dialogItem.volume || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, volume: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Frequency"
                value={dialogItem.frequency || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, frequency: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                select
                label="Quality"
                value={dialogItem.quality || 'Good'}
                onChange={(e) => setDialogItem({ ...dialogItem, quality: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </TextField>
            </Box>
          )}
          
          {dialogType === 'infrastructureAssessment' && (
            <Box>
              <TextField
                fullWidth
                label="Component"
                value={dialogItem.component || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, component: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={dialogItem.description || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Current Capacity"
                value={dialogItem.currentCapacity || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, currentCapacity: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Utilization Rate"
                value={dialogItem.utilizationRate || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, utilizationRate: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                select
                label="Status"
                value={dialogItem.status || 'Healthy'}
                onChange={(e) => setDialogItem({ ...dialogItem, status: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="Healthy">Healthy</option>
                <option value="Warning">Warning</option>
                <option value="Critical">Critical</option>
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDialog} variant="contained">
            {dialogItemIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CurrentLandscapeAnalysisPage;